import { fulfilled, pending, rejected, loadStatus } from 'lib/promiseMiddlewareHelpers'
import fetchJSON from 'lib/fetchJSON'
import Immutable, { Map, Record, List, OrderedSet } from 'immutable'
import { CREATE_GOAL, DELETE_GOAL, LOAD_GOAL } from '../goals'
import { buildQueryString } from 'lib/utils'

export const LOAD_GOAL_PARTICIPANT = 'goalParticipants/LOAD_GOAL_PARTICIPANT'
export const JOIN_GOAL = 'goalParticipants/JOIN_GOAL'
export const LOAD_PARTICIPANTS_FOR_GOAL = 'goalParticipants/LOAD_PARTICIPANTS_FOR_GOAL'
export const LOAD_INVITED_FOR_GOAL = 'goalParticipants/LOAD_INVITED_FOR_GOAL'
export const INVITE_TO_GOAL = 'goalParticipants/INVITE_TO_GOAL'
export const LEAVE_GOAL = 'goalParticipants/LEAVE_GOAL'
export const LOAD_INDEX_TRIPS = 'goalParticipants/LOAD_INDEX_TRIPS'
export const EXCLUDE_TRIP = 'goalParticipants/EXCLUDE_TRIP'
export const UNEXCLUDE_TRIP = 'goalParticipants/UNEXCLUDE_TRIP'

// state constants
const STATUS = {
  invited: 0,
  active: 1,
  completed: 2,
  failed: 3,
  declined: 4
}

const deserializeParticipant = (fromRails) => Immutable.fromJS(fromRails).merge({ loadStatus: loadStatus.fulfilled })

const initialState = Record({
  unscoped: Map(), // rank differs between unscoped and friend-filtered GPs, so
  friends: Map(), // we treat them as two distinct record types here
  invited: Map(), // because the other two only include active or completed, we collect invited/declined here
  trips: Map() // Map<List> of contributing trips keyed by goal participant ids
})()

const TripsRecord = Record({
  trips: List(),
  tripIds: OrderedSet(),
  resultsCount: null,
  nextPage: 0,
  loading: false,
  error: false
})

export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case fulfilled(LOAD_GOAL_PARTICIPANT): {
      const { meta: { participantId }, payload } = action
      const gp = deserializeParticipant(payload.goal_participant)
      return state.set('unscoped', state.unscoped.set(participantId, gp))
    }
    case fulfilled(JOIN_GOAL): {
      const { payload } = action
      const gp = deserializeParticipant(payload.goal_participant)
      const unscoped = state.unscoped.set(gp.get('id'), gp)
      const invited = state.invited.delete(gp.get('id'))
      return state.merge({ unscoped, invited })
    }
    case fulfilled(LOAD_PARTICIPANTS_FOR_GOAL): {
      const { payload, meta } = action
      const participants = Map(payload.results.map(gp => [gp.id, deserializeParticipant(gp)]))
      if (meta.friends) {
        return state.set('friends', state.friends.merge(participants))
      } else {
        return state.set('unscoped', state.unscoped.merge(participants))
      }
    }
    case fulfilled(LOAD_GOAL): {
      const { payload } = action
      if (!payload.goal_participant) {
        return state
      }
      const gp = deserializeParticipant(payload.goal_participant)
      switch (gp.get('status')) {
        case STATUS.invited:
          return state.set('invited', state.unscoped.set(gp.get('id'), gp))
        case STATUS.active:
        case STATUS.completed:
          return state.set('unscoped', state.unscoped.set(gp.get('id'), gp))
      }
      return state
    }
    case fulfilled(INVITE_TO_GOAL): {
      const { payload } = action
      const invited = Map(payload.invited.map(gp => [gp.id, deserializeParticipant(gp)]))
      return state.set('invited', state.invited.merge(invited))
    }
    case fulfilled(LOAD_INVITED_FOR_GOAL): {
      const { payload } = action
      const invited = Map(payload.results.map(gp => [gp.id, deserializeParticipant(gp)]))
      return state.set('invited', state.invited.merge(invited))
    }
    case fulfilled(CREATE_GOAL): {
      const { payload } = action
      const gp = deserializeParticipant(payload.goal_participant)
      return state.set('unscoped', state.unscoped.set(gp.get('id'), gp))
    }
    case fulfilled(DELETE_GOAL): {
      const { goalId } = action.meta
      const unscoped = state.unscoped.filter(gp => gp.getIn(['goal', 'id']) !== goalId)
      const friends = state.friends.filter(gp => gp.getIn(['goal', 'id']) !== goalId)
      return state.merge({ unscoped, friends })
    }
    case fulfilled(LEAVE_GOAL): {
      const { participantId } = action.meta
      const unscoped = state.unscoped.delete(participantId)
      const friends = state.friends.delete(participantId)
      return state.merge({ unscoped, friends })
    }
    case pending(LOAD_INDEX_TRIPS): {
      const { meta } = action
      const { participantId } = meta

      if (!state.trips.get(participantId)) {
        const trips = state.trips.set(participantId, new TripsRecord({ loading: true }))
        return state.merge({ trips })
      }
      return state.mergeDeep({ trips: { [participantId]: { loading: true } } })
    }
    case fulfilled(LOAD_INDEX_TRIPS): {
      const { payload, meta } = action
      const { participantId, page } = meta

      const tripsRecord = state.trips.get(participantId) || new TripsRecord()

      const participantTrips = Immutable.fromJS(payload.results)
      const tripIds = tripsRecord.tripIds.concat(participantTrips.map(t => t.get('id')))
      const resultsCount = payload.results_count
      const nextPage = resultsCount > tripIds.size ? page + 1 : -1

      const participantTripsWithUpdates = tripsRecord.get('trips').concat(participantTrips)
      return state.setIn(['trips', participantId],
        tripsRecord.merge({
          trips: participantTripsWithUpdates,
          tripIds,
          nextPage,
          resultsCount,
          loading: false,
          error: false
        })
      )
    }
    case rejected(LOAD_INDEX_TRIPS): {
      const { meta } = action
      const { participantId } = meta
      const trips = state.trips.get(participantId) || new TripsRecord()
      return state.mergeDeep({ trips: trips.merge({ loading: false, error: true }) })
    }
    case pending(EXCLUDE_TRIP):
    case pending(UNEXCLUDE_TRIP): {
      const { tripId, participantId } = action.meta
      const trips = state.trips.getIn([participantId, 'trips'], List())
      const tripIndex = trips.findIndex(t => t.get('id') === tripId)
      const isExcluded = action.type === pending(EXCLUDE_TRIP)
      if (tripIndex !== -1) {
        const newState = state.setIn(['trips', participantId, 'trips', tripIndex, 'is_excluded'], isExcluded)
        return newState
      }
      return state
    }
    default:
      return state
  }
}

export const loadGoalParticipant = (participantId) => ({
  type: LOAD_GOAL_PARTICIPANT,
  meta: { participantId },
  payload: fetchJSON(`/goal_participants/${participantId}.json`)
})

export const joinGoal = (goalId) => ({
  type: JOIN_GOAL,
  meta: { goalId },
  payload: fetchJSON(`/goals/${goalId}/join.json`, {})
})

export const inviteToGoal = (goalId, userIds) => ({
  type: INVITE_TO_GOAL,
  meta: { goalId, userIds },
  payload: fetchJSON(`/goals/${goalId}/invite.json`, { user_ids: userIds })
})

export const loadParticipantsForGoal = (goalId, { friends = false } = {}) => ({
  type: LOAD_PARTICIPANTS_FOR_GOAL,
  meta: { goalId, friends },
  payload: fetchJSON(`/goals/${goalId}/participants.json${friends ? '?friends=true' : ''}`)
})

export const loadInvitedForGoal = (goalId) => ({
  type: LOAD_INVITED_FOR_GOAL,
  meta: { goalId },
  payload: fetchJSON(`/goals/${goalId}/invited.json`)
})

export const leaveGoal = (participantId) => ({
  type: LEAVE_GOAL,
  meta: { participantId },
  payload: fetchJSON(`/goal_participants/${participantId}`, null, { method: 'DELETE' })
})

const PER_PAGE = 100
export const loadParticipantTrips = (participantId, page = 0) => {
  const query = buildQueryString({ limit: PER_PAGE, offset: page * PER_PAGE })
  return {
    type: LOAD_INDEX_TRIPS,
    meta: { participantId, page },
    payload: fetchJSON(`/goal_participants/${participantId}/trips.json?${query}`)
  }
}

export const excludeTrip = (participantId, tripId) => ({
  type: EXCLUDE_TRIP,
  meta: { participantId, tripId },
  payload: fetchJSON(`/goal_participants/${participantId}/exclude`, { trip_id: tripId })
})

export const unexcludeTrip = (participantId, tripId) => ({
  type: UNEXCLUDE_TRIP,
  meta: { participantId, tripId },
  payload: fetchJSON(`/goal_participants/${participantId}/unexclude`, { trip_id: tripId })
})

// selectors

export const selectParticipantsForGoal = (state, goalId, { friends = false } = {}) => {
  const participants = friends ? state.goalParticipants.friends : state.goalParticipants.unscoped
  return participants.filter(gp => gp.getIn(['goal', 'id']) === goalId)
}
